import {
	Accordion as ChakraAccordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	Flex,
	Text,
	AccordionIcon,
} from "@chakra-ui/react";
import { ReactElement, ReactNode, useState } from "react";

interface SingleAccordionProps {
	// Props overrides for default button/panel
	hoverColor?: string;
	buttonProps?: React.ComponentProps<typeof AccordionButton>;
	children?: ReactNode; // content inside the panel
	defaultButtonText?: string | ReactNode; // used if renderButtonContent not provided
	hideButton?: boolean;

	initialOpen?: boolean; // whether accordion starts open
	panelProps?: React.ComponentProps<typeof AccordionPanel>;

	onAccordionChange?: (isOpen: boolean) => void;
	// Optionally override the entire button/panel
	renderButtonContent?: (isOpen: boolean) => ReactNode;

	renderPanelContent?: (
		close: () => void,
		open: () => void
	) => ReactElement<typeof AccordionPanel>;
}

export interface SingleAccordionRef {
	isOpen: boolean;
	close: () => void;
	open: () => void;
	toggle: () => void;
}

export const SingleAccordion: React.FC<SingleAccordionProps> = ({
	children,
	hideButton = false,
	hoverColor = "#dbeafe",
	initialOpen = false,
	onAccordionChange,
	buttonProps,
	panelProps,
	renderButtonContent,
	renderPanelContent,
	defaultButtonText = "Click to expand",
}) => {
	const [isOpen, setIsOpen] = useState(initialOpen);

	const handleChange = () => {
		setIsOpenState(!isOpen);
	};

	const setIsOpenState = (newState: boolean) => {
		setIsOpen(newState);
		onAccordionChange?.(newState);
	};

	const close = () => setIsOpenState(false);
	const open = () => setIsOpenState(true);

	return (
		<ChakraAccordion
			allowToggle
			index={isOpen ? 0 : -1}
		>
			<AccordionItem border="none">
				{!hideButton && (
					<AccordionButton
						px={0}
						style={{ paddingLeft: "5px", borderRadius: "5px" }}
						_hover={{ bgColor: hoverColor }}
						onClick={handleChange}
						{...buttonProps}
					>
						{renderButtonContent ? (
							renderButtonContent(isOpen)
						) : (
							<Flex
								w="full"
								justify="flex-start"
							>
								<div className="inline-block rounded-md bg-blue-100 p-1 text-gray-500">
									{defaultButtonText}
									<AccordionIcon paddingLeft={1} />
								</div>
							</Flex>
						)}
					</AccordionButton>
				)}
				<AccordionPanel
					px={0}
					pt={4}
					{...panelProps}
				>
					{renderPanelContent ? renderPanelContent(close, open) : children}
				</AccordionPanel>
			</AccordionItem>
		</ChakraAccordion>
	);
};
